/**
 * @module fileNaming
 * @description Utilitaires pour générer les noms de fichiers des documents
 *
 * Génère des noms de fichiers structurés pour l'export :
 * - Numérotation globale continue (frontmatter -> bodymatter -> backmatter)
 * - Nom basé sur le type de document
 * - Suffixe numérique si plusieurs documents du même type
 *
 * @example
 * // Génère: "01-preface.md", "02-introduction.md", "03-chapter-1.md", "04-chapter-2.md"
 * const filenames = generateFilenames(documents);
 */

import type { Document, DocumentSection } from "../types";

/**
 * Result of filename generation for a document
 */
export interface FilenameResult {
  doc: Document;
  filename: string;
  globalIndex: number;
  section: DocumentSection;
}

/**
 * Preview of filenames grouped by section
 */
export interface FilenamePreview {
  frontmatter: FilenameResult[];
  bodymatter: FilenameResult[];
  backmatter: FilenameResult[];
}

/**
 * Ordre des sections pour l'export
 */
const SECTION_ORDER: DocumentSection[] = [
  "frontmatter",
  "bodymatter",
  "backmatter",
];

/**
 * Normalise une chaîne pour créer un nom de fichier valide
 * Supprime les accents, caractères spéciaux et espaces
 *
 * @example
 * normalizeForFilename("Préface de l'auteur") // "preface-de-l-auteur"
 * normalizeForFilename("Chapitre 1 : Introduction") // "chapitre-1-introduction"
 */
export function normalizeForFilename(str: string): string {
  if (!str) return "document";

  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Supprimer les accents
    .replace(/[^a-z0-9]+/g, "-") // Remplacer les caractères spéciaux par des tirets
    .replace(/^-+|-+$/g, "") // Supprimer les tirets en début/fin
    .replace(/-+/g, "-"); // Éviter les tirets multiples
}

/**
 * Trie les documents par section puis par ordre, puis par titre (pour stabilité)
 */
function sortDocumentsForExport(documents: Document[]): Document[] {
  return [...documents]
    .filter((doc) => doc.documentType) // Exclure les documents sans type
    .sort((a, b) => {
      // D'abord par section
      const sectionA = SECTION_ORDER.indexOf(a.documentSection || "bodymatter");
      const sectionB = SECTION_ORDER.indexOf(b.documentSection || "bodymatter");

      if (sectionA !== sectionB) {
        return sectionA - sectionB;
      }

      // Ensuite par ordre dans la section
      const orderA = a.order ?? Infinity;
      const orderB = b.order ?? Infinity;

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      // Enfin par titre pour garantir un tri stable
      return (a.title || "").localeCompare(b.title || "");
    });
}

/**
 * Génère les noms de fichiers pour tous les documents
 *
 * @example
 * const results = generateFilenames(documents);
 * // [
 * //   { doc: {...}, filename: "01-preface.md", globalIndex: 0 },
 * //   { doc: {...}, filename: "02-introduction.md", globalIndex: 1 },
 * //   { doc: {...}, filename: "03-chapter-1.md", globalIndex: 2 },
 * //   { doc: {...}, filename: "04-chapter-2.md", globalIndex: 3 },
 * // ]
 */
export function generateFilenames(documents: Document[]): FilenameResult[] {
  const sortedDocs = sortDocumentsForExport(documents);

  // Compter les occurrences de chaque type de document
  const typeCounts: Record<string, number> = {};

  return sortedDocs.map((doc, globalIndex) => {
    const docType = doc.documentType || "document";
    const normalizedType = normalizeForFilename(docType);

    // Incrémenter le compteur pour ce type
    typeCounts[docType] = (typeCounts[docType] || 0) + 1;
    const typeOccurrence = typeCounts[docType];

    // Compter combien de documents de ce type il y a au total
    const totalOfType = sortedDocs.filter(
      (d) => d.documentType === docType,
    ).length;

    // Formater le numéro global avec padding (01, 02, 03, ...)
    const paddedIndex = String(globalIndex + 1).padStart(2, "0");

    // Générer le nom de fichier
    let filename: string;
    if (totalOfType > 1) {
      // S'il y a plusieurs documents du même type, ajouter le numéro
      filename = `${paddedIndex}-${normalizedType}-${typeOccurrence}.md`;
    } else {
      // Sinon, juste le type
      filename = `${paddedIndex}-${normalizedType}.md`;
    }

    return {
      doc,
      filename,
      globalIndex,
      section: (doc.documentSection || "bodymatter") as DocumentSection,
    };
  });
}

/**
 * Génère un aperçu des noms de fichiers pour l'affichage
 *
 * @example
 * const preview = generateFilenamePreview(documents);
 * // {
 * //   frontmatter: [{ doc, filename: "01-preface.md", ... }],
 * //   bodymatter: [{ doc, filename: "03-chapter-1.md", ... }],
 * //   backmatter: [{ doc, filename: "06-bibliography.md", ... }]
 * // }
 */
export function generateFilenamePreview(
  documents: Document[],
): FilenamePreview {
  const filenames = generateFilenames(documents);

  return {
    frontmatter: filenames.filter((f) => f.section === "frontmatter"),
    bodymatter: filenames.filter((f) => f.section === "bodymatter"),
    backmatter: filenames.filter((f) => f.section === "backmatter"),
  };
}

/**
 * Obtient le nom de fichier pour un document spécifique
 */
export function getDocumentFilename(
  doc: Document,
  allDocuments: Document[],
): string | null {
  if (!doc.documentType) return null;

  const filenames = generateFilenames(allDocuments);
  const found = filenames.find((f) => f.doc.id === doc.id);

  return found ? found.filename : null;
}

export default {
  normalizeForFilename,
  generateFilenames,
  generateFilenamePreview,
  getDocumentFilename,
};
